import { getMessaging, getToken, isSupported } from '@firebase/messaging';
import { captureException } from '@sentry/react';
import { sendDeviceToken } from 'apis/deviceToken.api';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  DocumentData,
  FirestoreDataConverter,
  getFirestore,
} from 'firebase/firestore';
import { Messaging } from 'firebase/messaging';
import { NOTIFICATION_PERMISSION_LOCAL_STORAGE_KEY } from 'shared/constants';
import Swal from 'sweetalert2';

// Khởi tạo Firebase app
const firebaseConfig = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG);
const app = initializeApp(firebaseConfig);

// Xuất các module Firebase
export const vapidKey = import.meta.env.VITE_VAPID_KEY;
export const auth = getAuth(app);
export const firestore = getFirestore(app);

// Converter cho Firestore
export const converter = <T>(): FirestoreDataConverter<T> => ({
  toFirestore: (data): DocumentData => data as DocumentData,
  fromFirestore: (snapshot): T => snapshot.data() as T,
});

// Khởi tạo Messaging
export async function initializeMessaging(): Promise<Messaging | null> {
  try {
    const isSupportedBrowser = await isSupported();
    if (!isSupportedBrowser) {
      console.warn('[Firebase] Messaging not supported in this browser');
      return null;
    }
    return getMessaging(app);
  } catch (error) {
    console.error('[Firebase] Failed to initialize Messaging:', error);
    captureException(error);
    return null;
  }
}
// Yêu cầu token và gửi đến server
async function requestToken(
  messaging: Messaging,
  serviceWorkerRegistration: ServiceWorkerRegistration
): Promise<void> {
  try {
    const currentToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    });
    if (currentToken) {
      await sendDeviceToken(currentToken);
      console.log('[Firebase] Token sent successfully:', currentToken);
    } else {
      console.warn('[Firebase] No token received');
    }
  } catch (error) {
    console.error('[Firebase] Error requesting token:', error);
    captureException(error);
  }
}
// Yêu cầu quyền thông báo
async function requestPermission(
  messaging: Messaging,
  serviceWorkerRegistration: ServiceWorkerRegistration
): Promise<boolean> {
  try {
    if (Notification.permission === 'granted') {
      await requestToken(messaging, serviceWorkerRegistration);
      return true;
    }

    const result = await Swal.fire({
      title: 'Bật thông báo',
      text: 'Hãy bật thông báo để nhận cập nhật về đơn phòng mới nhất từ Myla nhé!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Bật thông báo',
      cancelButtonText: 'Từ chối',
      reverseButtons: true,
      customClass: {
        cancelButton: 'button button-secondary',
        confirmButton: 'button button-success',
      },
      allowOutsideClick: false,
      timer: 30000, // Tự đóng sau 30 giây nếu người dùng không tương tác
    });

    const permissionGranted = result.isConfirmed;
    localStorage.setItem(
      NOTIFICATION_PERMISSION_LOCAL_STORAGE_KEY,
      String(permissionGranted)
    );

    if (!permissionGranted) {
      console.log('[Firebase] User declined notification permission');
      return false;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await requestToken(messaging, serviceWorkerRegistration);
      return true;
    }
    console.warn('[Firebase] Notification permission denied');
    return false;
  } catch (error) {
    console.error('[Firebase] Error requesting permission:', error);
    captureException(error);
    return false;
  }
}

// Hàm chính để yêu cầu quyền thông báo
export async function requestNotificationPermission(
  serviceWorkerRegistration: ServiceWorkerRegistration
): Promise<boolean> {
  const messaging = await initializeMessaging();
  if (!messaging) {
    return false;
  }

  if (serviceWorkerRegistration.active) {
    return requestPermission(messaging, serviceWorkerRegistration);
  }

  if (serviceWorkerRegistration.installing) {
    return new Promise((resolve) => {
      const handleStateChange = async () => {
        if (serviceWorkerRegistration.active) {
          serviceWorkerRegistration.removeEventListener(
            'statechange',
            handleStateChange
          );
          resolve(
            await requestPermission(messaging, serviceWorkerRegistration)
          );
        }
      };
      serviceWorkerRegistration.addEventListener(
        'statechange',
        handleStateChange
      );
    });
  }

  console.warn('[Firebase] No active or installing Service Worker found');
  return false;
}

export default app;
